import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Shield, Mail, MapPin, Clock, Eye, Edit, Trash2, Download, AlertCircle, Lock, Cookie, ExternalLink, Users, FileText, LucideIcon } from 'lucide-react';
import useThemeStore from '../store/themeStore';
import { ThemeProps } from '../utils/theme';
import { isEmbeddedInFrame } from '../utils/isEmbeddedInFrame';
import backgroundPattern from '../images/vesta/vesta-property-pattern-01-teal-bg.svg';

interface SectionProps {
  icon: LucideIcon;
  title: string;
  children: React.ReactNode;
  className?: string;
}

interface RightCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  highlight?: boolean;
}

interface PrivacyPolicyProps {
  isModal?: boolean;
}

const PrivacyPolicy = ({ isModal = false }: PrivacyPolicyProps) => {
  const getThemes = useThemeStore((state) => state.theme as unknown as ThemeProps);
  const [isInIframe, setIsInIframe] = useState(false);

  useEffect(() => {
    setIsInIframe(isEmbeddedInFrame());
  }, []);
  

  // White color detection function
  const isWhiteOrShade = (color: string): boolean => {
    const s = color.trim().toLowerCase();
    
    if (s === "white" || s === "#ffffff" || s === "#fff" || s === "rgb(255, 255, 255)" || s === "rgba(255, 255, 255, 1)") {
      return true;
    }
    
    if (s.startsWith("#") && s.length === 7) {
      const hex = s.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 240;
    }
    
    if (s.startsWith("rgb")) {
      const matches = s.match(/\d+/g);
      if (matches && matches.length >= 3) {
        const r = parseInt(matches[0]);
        const g = parseInt(matches[1]);
        const b = parseInt(matches[2]);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness > 240;
      }
    }
    
    return false;
  };

  // Get contrasting text color for better visibility
  const getContrastColor = (backgroundColor: string): string => {
    return isWhiteOrShade(backgroundColor) ? "#1f2937" : "#ffffff";
  };

  const Section = ({ icon: Icon, title, children, className = "" }: SectionProps) => {
    if (isModal) {
      return (
        <div className={`bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 ease-in-out ${className}`}>
          <div
            className="px-4 py-3"
            style={{ background: getThemes?.color1 }}
          >
            <div className="flex items-center">
              <Icon className="w-5 h-5 mr-2" style={{ color: getContrastColor(getThemes?.color1 || "") }} />
              <h2 
                className="text-lg font-semibold"
                style={{ color: getContrastColor(getThemes?.color1 || "") }}
              >
                {title}
              </h2>
            </div>
          </div>
          <div className="p-4 text-sm text-gray-700">
            {children}
          </div>
        </div>
      );
    }
    
    return (
      <div className={`bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl hover:bg-gray-50 transition-all duration-300 ease-in-out ${className}`}>
        <div
          className="px-6 py-4"
          style={{ background: getThemes?.color1 }}
        >
          <div className="flex items-center">
            <Icon className="w-6 h-6 mr-3" style={{ color: getContrastColor(getThemes?.color1 || "") }} />
            <h2 
              className="text-xl font-bold"
              style={{ color: getContrastColor(getThemes?.color1 || "") }}
            >
              {title}
            </h2>
          </div>
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    );
  };

  const RightCard = ({ icon: Icon, title, description, highlight = false }: RightCardProps) => (
    <div className={`rounded-lg ${isModal ? 'p-2 mb-2' : 'p-4 mb-3'} ${highlight ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
      <div className="flex items-start gap-2">
        <div className={`${isModal ? 'p-1 rounded' : 'p-1.5 rounded-md'} ${highlight ? 'bg-blue-100' : 'bg-gray-200'}`}>
          <Icon className="w-4 h-4 text-gray-600" />
        </div>
        <div>
          <h3 className={`font-semibold text-gray-900 ${isModal ? 'text-xs mb-1' : 'text-sm mb-1'}`}>{title}</h3>
          <p className={`text-gray-600 leading-relaxed ${isModal ? 'text-xs' : 'text-xs'}`}>{description}</p>
        </div>
      </div>
    </div>
  );

  // Function to render the main content
  const renderContent = () => (
    <div className={`mx-auto ${isModal ? 'space-y-2' : 'space-y-8'} ${isModal ? 'max-w-none' : 'max-w-7xl'}`}>
      {/* Header - Only show on full page, not in modal */}
      {!isModal && (
        <div className="text-center bg-white p-6 mb-8 rounded-lg shadow-lg mt-24">
          <h1 className="text-4xl font-bold mb-2 text-gray-900">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-700">
            Last updated: 13/08/2025
          </p>
        </div>
      )}

      {/* Last updated date for modal */}
      {isModal && (
        <div className="text-center mb-2">
          <p className="text-sm text-gray-600">
            Last updated: 13/08/2025
          </p>
        </div>
      )}

      {/* Introduction Card */}
      <div className={`${isModal ? 'bg-gray-50 rounded-lg p-4' : 'bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 ease-in-out'}`}>
        <div className={`flex flex-col items-center text-center ${isModal ? 'gap-2 mb-3' : 'gap-4 mb-6'}`}>
          <div 
            className={`${isModal ? 'p-2 rounded-lg' : 'p-3 rounded-xl'}`}
            style={{ backgroundColor: getThemes?.color1 }}
          >
            <Shield className={`${isModal ? 'w-5 h-5' : 'w-8 h-8'}`} style={{ color: getContrastColor(getThemes?.color1 || "") }} />
          </div>
          <div>
            <h2 className={`font-bold text-gray-900 mb-2 ${isModal ? 'text-lg' : 'text-2xl'}`}>Your Privacy Matters</h2>
            <p className={`text-gray-600 ${isModal ? 'text-sm' : 'text-lg'}`}>
              We are committed to protecting your personal information and being transparent about how we collect, use, and share it.
            </p>
          </div>
        </div>
        <div className={`${isModal ? 'bg-white rounded p-3' : 'bg-gray-50 rounded-lg p-6'}`}>
          <p className={`text-gray-700 leading-relaxed text-center ${isModal ? 'text-sm' : ''}`}>
            This Privacy Policy explains how {getThemes?.name || 'My Property Technology'} ("we," "our," or "us") collects, uses, discloses, and safeguards your information when you use our property investment platform and related services (collectively, the "Service").
          </p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className={`grid grid-cols-1 ${isModal ? 'gap-4' : 'lg:grid-cols-3 gap-8'}`}>
        {/* Left Column - Main Content */}
        <div className={`${isModal ? 'space-y-4' : 'lg:col-span-2 space-y-6'}`}>
          <Section icon={Users} title="Who We Are">
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                {getThemes?.name || 'My Property Technology'} is a property investment platform that helps investors find, analyse, and manage property investments.
                {/* We are registered in England and Wales under company number 16328866. */}
              </p>
              {/* Registered Address and Company Details commented out for now
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoCard
                  icon={MapPin}
                  title="Registered Address"
                  items={["LGF, 235-237 Vauxhall Bridge road", "London, SW1V 1EJ", "United Kingdom"]}
                />
                <InfoCard
                  icon={FileText}
                  title="Company Details"
                  items={["Company Number: 16328866", "Registered in England & Wales", "VAT Number: Available on request"]}
                />
              </div>
              */}
            </div>
          </Section>

          <Section icon={Eye} title="Information We Collect">
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Personal Information</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Name, email address, and contact details</li>
                  <li>• Property investment preferences and criteria</li>
                  <li>• Financial information for investment analysis</li>
                  <li>• Communication preferences and history</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Technical Information</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• IP address and device information</li>
                  <li>• Browser type and version</li>
                  <li>• Usage patterns and preferences</li>
                  <li>• Cookies and similar technologies</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Information from Third Parties</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• App Store data from distribution platforms</li>
                  <li>• Social media information if accounts connected</li>
                  <li>• Analytics from third-party services</li>
                </ul>
              </div>
            </div>
          </Section>

          <Section icon={Edit} title="How We Use Your Information">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Service Delivery</h3>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li>• Provide property investment opportunities</li>
                  <li>• Process investment applications</li>
                  <li>• Send property updates and alerts</li>
                  <li>• Manage your account and preferences</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Communication</h3>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li>• Respond to your inquiries</li>
                  <li>• Send important service updates</li>
                  <li>• Provide customer support</li>
                  <li>• Share relevant property news</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Legal Basis for Processing</h3>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li>• Consent: Marketing communications</li>
                  <li>• Contract: Service provision</li>
                  <li>• Legitimate Interest: Service improvement</li>
                  <li>• Legal Obligation: Compliance</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Service Improvement</h3>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li>• Analyse usage patterns</li>
                  <li>• Develop new features</li>
                  <li>• Conduct research and analytics</li>
                  <li>• Prevent fraud and ensure security</li>
                </ul>
              </div>
            </div>
          </Section>

          <Section icon={Users} title="Data Sharing">
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                We may share your personal information with trusted third parties in specific circumstances:
              </p>
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Service Providers</h3>
                  <p className="text-sm text-gray-700">White Label Property (technology partner), payment processors, cloud hosting providers, and analytics services.</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Legal Requirements</h3>
                  <p className="text-sm text-gray-700">Law enforcement agencies, regulatory authorities, and courts when required by law.</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Business Transfers</h3>
                  <p className="text-sm text-gray-700">In case of merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</p>
                </div>
              </div>
            </div>
          </Section>

          <Section icon={ExternalLink} title="International Data Transfers">
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                Some of our service providers may be located outside the UK. When we transfer data internationally, we ensure adequate protection through:
              </p>
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Adequacy Decisions</h3>
                  <p className="text-sm text-gray-700">Transfers to countries deemed adequate by the UK Government.</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Standard Contractual Clauses</h3>
                  <p className="text-sm text-gray-700">EU/UK approved contractual protections for international transfers.</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Certification Schemes</h3>
                  <p className="text-sm text-gray-700">Recognised international data protection certifications.</p>
                </div>
              </div>
            </div>
          </Section>

          <Section icon={Lock} title="Data Security">
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Technical Safeguards</h3>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• SSL encryption for data transmission</li>
                    <li>• Secure data storage and backup</li>
                    <li>• Regular security updates and monitoring</li>
                    <li>• Access controls and authentication</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Organisational Measures</h3>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• Staff training on data protection</li>
                    <li>• Confidentiality agreements</li>
                    <li>• Regular security audits</li>
                    <li>• Incident response procedures</li>
                  </ul>
                </div>
              </div>
            </div>
          </Section>

          <Section icon={Clock} title="Data Retention">
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
              </p>
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Account Information</h3>
                  <p className="text-sm text-gray-700">Retained while your account is active and for 7 years after closure for legal and regulatory purposes.</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Investment Records</h3>
                  <p className="text-sm text-gray-700">Retained for 7 years after the end of the investment relationship as required by financial regulations.</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Marketing Communications</h3>
                  <p className="text-sm text-gray-700">Retained until you opt out or for 3 years of inactivity, whichever comes first.</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Usage Analytics</h3>
                  <p className="text-sm text-gray-700">Aggregated data may be retained indefinitely after anonymisation.</p>
                </div>
              </div>
            </div>
          </Section>

          <Section icon={AlertCircle} title="Data Breach & Automated Decisions">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Data Breach Notification</h3>
                  <p className="text-sm text-gray-700 mb-2">In the event of a data breach that poses a high risk to your rights and freedoms, we will:</p>
                  <ul className="space-y-1 text-xs text-gray-600">
                    <li>• Notify the ICO within 72 hours of becoming aware of the breach</li>
                    <li>• Inform affected individuals without undue delay</li>
                    <li>• Provide information about the nature and steps being taken</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Automated Decision Making</h3>
                  <p className="text-sm text-gray-700">We may use automated systems to detect fraud, personalise content, and analyse usage patterns. You have the right not to be subject to decisions based solely on automated processing that significantly affect you.</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Children's Privacy</h3>
                  <p className="text-sm text-gray-700">Our Service is not intended for children under 13. We do not knowingly collect personal data from children under 13.</p>
                </div>
              </div>
            </div>
          </Section>
        </div>

        {/* Right Column - Your Rights & Contact */}
        <div className={`${isModal ? 'space-y-4' : 'space-y-6'}`}>
          <Section icon={Download} title="Your Rights">
            <div className={`${isModal ? 'grid grid-cols-1 md:grid-cols-2 gap-3' : 'space-y-3'}`}>
              <RightCard
                icon={Eye}
                title="Right to Access"
                description="Request a copy of your personal data"
              />
              <RightCard
                icon={Edit}
                title="Right to Rectification"
                description="Correct inaccurate or incomplete data"
              />
              <RightCard
                icon={Trash2}
                title="Right to Erasure"
                description="Request deletion of your personal data"
              />
              <RightCard
                icon={Lock}
                title="Right to Restrict Processing"
                description="Limit how we use your data"
              />
              <RightCard
                icon={Download}
                title="Right to Data Portability"
                description="Receive your data in a structured format"
              />
              <RightCard
                icon={AlertCircle}
                title="Right to Object"
                description="Object to processing for marketing purposes"
              />
              <RightCard
                icon={FileText}
                title="Right to Lodge Complaint"
                description="Complain to the ICO if data is mishandled"
              />
              <RightCard
                icon={AlertCircle}
                title="Right to Withdraw Consent"
                description="Withdraw consent where it's the legal basis"
                highlight={true}
              />
            </div>
          </Section>

          <Section icon={Mail} title="Contact & ICO Information">
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:
              </p>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Data Protection Officer</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-gray-600" />
                      <span className="text-sm text-gray-700">{getThemes?.adminEmail ?? '—'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-gray-600" />
                      <span className="text-sm text-gray-700">
                        {[getThemes?.adminFirstName, getThemes?.adminLastName].filter(Boolean).join(' ') || '—'}, DPO
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">General Enquiries</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-gray-600" />
                      <span className="text-sm text-gray-700">{getThemes?.adminEmail ?? '—'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-gray-600" />
                      <span className="text-sm text-gray-700">
                        {[getThemes?.adminFirstName, getThemes?.adminLastName].filter(Boolean).join(' ') || '—'}
                      </span>
                    </div>
                    {/* Address commented out - contact is from theme
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-gray-600" />
                      <span className="text-sm text-gray-700">LGF, 235-237 Vauxhall Bridge road, London, SW1V 1EJ</span>
                    </div>
                    */}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Information Commissioner's Office (ICO)</h3>
                  <div className="space-y-1 text-sm text-gray-700">
                    <div>Wycliffe House, Water Lane, Wilmslow, Cheshire SK9 5AF</div>
                    <div>Phone: 0303 123 1113</div>
                    <div>Website: www.ico.org.uk</div>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          <Section icon={Cookie} title="Cookies">
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                We use cookies and similar technologies to enhance your experience, analyse usage, and provide personalised content.
              </p>
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Essential Cookies</h3>
                  <p className="text-sm text-gray-700">Required for basic site functionality and security.</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Analytics Cookies</h3>
                  <p className="text-sm text-gray-700">Help us understand how you use our platform to improve our services.</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Marketing Cookies</h3>
                  <p className="text-sm text-gray-700">Used to show you relevant property recommendations and advertisements.</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                You can manage cookie preferences through your browser settings and our cookie consent banner.
              </p>
            </div>
          </Section>

          <Section icon={ExternalLink} title="Third-Party Links">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center justify-center gap-3 mb-3">
                <div 
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: getThemes?.color1 || '#8b5cf6' }}
                >
                  <ExternalLink className="w-5 h-5" style={{ color: getContrastColor(getThemes?.color1 || '#8b5cf6') }} />
                </div>
                <h3 className="font-semibold text-black">External Services</h3>
              </div>
              <p className="text-sm leading-relaxed text-black text-center">
                Our Service may contain links to third-party websites. This Privacy Policy does not apply to external sites. Please read their privacy policies.
              </p>
            </div>
          </Section>

          <Section icon={AlertCircle} title="Policy Updates">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center justify-center gap-3 mb-3">
                <div 
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: getThemes?.color1 || '#8b5cf6' }}
                >
                  <AlertCircle className="w-5 h-5" style={{ color: getContrastColor(getThemes?.color1 || '#8b5cf6') }} />
                </div>
                <h3 className="font-semibold text-black">Policy Updates</h3>
              </div>
              <p className="text-sm leading-relaxed text-black text-center mb-3">
                We may update this Privacy Policy periodically. We'll notify you of material changes via:
              </p>
              <div className="space-y-2">
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-sm text-gray-700">Email notifications for significant changes</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-sm text-gray-700">In-app notifications when you next visit</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-sm text-gray-700">Updated "Last updated" date at the top</p>
                </div>
              </div>
            </div>
          </Section>
        </div>
      </div>

      {/* Footer */}
      <div className={`${isModal ? 'bg-gray-50 rounded-lg p-4 mt-4' : 'bg-white rounded-xl shadow-lg p-6 mt-8 hover:shadow-2xl transition-all duration-300 ease-in-out'}`}>
        <div className="text-center">
          <p className={`text-gray-700 leading-relaxed ${isModal ? 'text-sm' : ''}`}>
            By using our Service, you acknowledge that you have read, understood, and agree to this Privacy Policy and our Terms & Conditions.
          </p>
        </div>
      </div>
    </div>
  );

  // If used as modal content, return only the content without header/footer wrapper
  if (isModal || isInIframe) {
    return (
      <div className="p-6" style={{ backgroundColor: 'white' }}>
        <Helmet>
          <title>Privacy Policy | {getThemes?.name || 'Property Investment Platform'}</title>
          <meta name="description" content="Our privacy policy and data handling practices" />
        </Helmet>
        {renderContent()}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Privacy Policy | {getThemes?.name || 'Property Investment Platform'}</title>
        <meta name="description" content="Our privacy policy and data handling practices" />
      </Helmet>

      {/* Background Pattern */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-5"
        style={{ backgroundImage: `url(${backgroundPattern})` }}
      />

      {/* Main Content */}
      <div className="relative z-10 flex-1 py-12 px-4 sm:px-6 lg:px-8">
        {renderContent()}
      </div>
    </div>
  );
};

export default PrivacyPolicy;